import { DataManager } from 'utils/DataManager/dataManager';
import {
    IDiagnosticSummary,
    IDetailedEvent,
    IEmployeeComparisonResults,
    IEmailIssues,
    IError,
    IListenerErrors, 
    IDuplicateJob } from 'typings/diagnostics';
import { isValidNumber } from 'utils/numberUtils';

class DiagnosticsManager extends DataManager {
    getDiagnostics(masterAccountId: string): Promise<Nullable<IDiagnosticSummary>> {
        const receive = () => this._getDataRequest<IDiagnosticSummary>(`/${masterAccountId}/diagnostics`);
        return this.getData<IDiagnosticSummary>(receive, false);
    }

    async updateBasePayRangeStatus(masterAccountId: string, cycleId: NullOrUndefined<number>): Promise<void> {
        if (!this.isNumberIdValid(cycleId)) {
            return;
        }

        const receive = () => this._postDataRequest<null, void>(
            `/${masterAccountId}/diagnostics/${cycleId}/basePayRange`,
            null);
        await this.getData<void>(receive, false);
    }

    async regenerateWorkflow(masterAccountId: string, cycleId: NullOrUndefined<number>): Promise<void> {
        if (!this.isNumberIdValid(cycleId)) {
            return;
        }

        const receive = () => this._postDataRequest<null, void>(
            `/${masterAccountId}/diagnostics/${cycleId}/regenerateworkflow`,
            null);
        await this.getData<void>(receive, false);
    }

    async resetUserIds(masterAccountId: string): Promise<void> {
        const receive = () => this._postDataRequest<null, void>(
            `/${masterAccountId}/diagnostics/resetUserIds`,
            null);
        await this.getData<void>(receive, false);
    }

    async reprocesspayHistory(masterAccountId: string): Promise<void> {
        const receive = () => this._postDataRequest<null, void>(
            `/${masterAccountId}/diagnostics/reprocessPayHistory`,
            null);
        await this.getData<void>(receive, false);
    }

    async reprocessEmployees(masterAccountId: string): Promise<void> {
        const receive = () => this._postDataRequest<null, void>(
            `/${masterAccountId}/diagnostics/reprocessAccount`,
            null);
        await this.getData<void>(receive, false);
    }

    async flushRedisCache(masterAccountId: string): Promise<void> {
        const receive = () => this._deleteDataRequest<void>(
            `/${masterAccountId}/diagnostics/cache`,
            null);
        await this.getData<void>(receive, false);
    }

    async backfill(masterAccountId: string): Promise<void> {
        const receive = () => this._deleteDataRequest<void>(
            `/${masterAccountId}/backfill`,
            null);
        await this.getData<void>(receive, false);
    }

    getBackfillLink(masterAccountId: string): Promise<Nullable<string>> {
        const receive = () => this._getDataRequest<string>(`/${masterAccountId}/diagnostics/backfill`);
        return this.getData<string>(receive, false);
    }

    async notifyManagers(masterAccountId: string, cycleId: NullOrUndefined<number>): Promise<void> {
        if (!this.isNumberIdValid(cycleId)) {
            return;
        }

        const receive = () => this._postDataRequest<null, void>(
            `/${masterAccountId}/diagnostics/${cycleId}/notifyManagers`,
            null);
        await this.getData<void>(receive, false);
    }

    async closeCycle(masterAccountId: string, cycleId: NullOrUndefined<number>): Promise<void> {
        if (!this.isNumberIdValid(cycleId)) {
            return;
        }

        const receive = () => this._postDataRequest<null, void>(
            `/${masterAccountId}/diagnostics/${cycleId}/closeCycle`,
            null);
        await this.getData<void>(receive, false);
    }

    async publishCycle(masterAccountId: string, cycleId?: Nullable<number>): Promise<void> {
        if (!this.isNumberIdValid(cycleId)) {
            return;
        }

        const receive = () => this._postDataRequest<null, void>(
            `/${masterAccountId}/diagnostics/${cycleId}/publishCycle`,
            null);
        await this.getData<void>(receive, false);
    }

    async forceDeleteCycle(masterAccountId: string, cycleId: NullOrUndefined<number>): Promise<void> {
        if (!this.isNumberIdValid(cycleId)) {
            return;
        }

        const receive = () => this._deleteDataRequest<void>(
            `/${masterAccountId}/diagnostics/${cycleId}/forceDeleteCycle`,
            null);
        await this.getData<void>(receive, false);
    }

    async forceDeleteBudget(
        masterAccountId: string,
        cycleId: NullOrUndefined<number>,
        budgetCycleId: NullOrUndefined<number>): Promise<void> {
        if (!this.isNumberIdValid(cycleId) || !this.isNumberIdValid(budgetCycleId)) {
            return;
        }

        const receive = () => this._deleteDataRequest<void>(
            `/${masterAccountId}/diagnostics/cycle/${cycleId}/budget/${budgetCycleId}/forceDeleteBudget`,
            null);
        await this.getData<void>(receive, false);
    }

    async reopenClosedCycle(masterAccountId: string, cycleId: NullOrUndefined<number>): Promise<void> {
        if (!this.isNumberIdValid(cycleId)) {
            return;
        }

        const receive = () => this._postDataRequest<null, void>(
            `/${masterAccountId}/diagnostics/${cycleId}/revertClosedCycle`,
            null);
        await this.getData<void>(receive, false);
    }

    compareEmployees(masterAccountId: string): Promise<Nullable<IEmployeeComparisonResults>> {
        const receive = () => this._getDataRequest<IEmployeeComparisonResults>(
            `/${masterAccountId}/diagnostics/employees/compare`);
        return this.getData<IEmployeeComparisonResults>(receive, false);
    }

    getEmailIssues(masterAccountId: string): Promise<Nullable<IEmailIssues>> {
        const receive = () => this._getDataRequest<IEmailIssues>(
            `/${masterAccountId}/emails`);
        return this.getData<IEmailIssues>(receive, false);
    }

    async getValidationErrors(masterAccountId: string): Promise<IError[]> {
        const receive = () => this._getDataRequest<IError[]>(
            `/${masterAccountId}/validation`);
        const errors = await this.getData<IError[]>(receive, false);
        return errors ?? [];
    }

    async getListenerErrors(masterAccountId: string): Promise<IListenerErrors[]> {
        const receive = () => this._getDataRequest<IListenerErrors[]>(
            `/${masterAccountId}/listenerLogs`);
        const errors = await this.getData<IListenerErrors[]>(receive, false);
        return errors ?? [];
    }

    getDuplicateJobs(masterAccountId: string): Promise<Nullable<IDuplicateJob[]>> {
        const receive = () => this._getDataRequest<IDuplicateJob[]>(
            `/${masterAccountId}/diagnostics/duplicateJobs`);
        return this.getData<IDuplicateJob[]>(receive, false);
    }

    getEmployeeEvents(masterAccountId: string, employeeSourceId: string): Promise<Nullable<IDetailedEvent[]>> {
        const receive = () =>
            this._getDataRequest<IDetailedEvent[]>(
                `/${masterAccountId}/diagnostics/employeeevents/${encodeURIComponent(employeeSourceId)}`);
        return this.getData<IDetailedEvent[]>(receive, false);
    }

    private isNumberIdValid(Id: NullOrUndefined<number>): boolean {
        return (!!Id && isValidNumber(Id)) && Id > 0;
    }
}

export { DiagnosticsManager };
export default DiagnosticsManager;